import type { PeriodType } from '../models/employee-period-report.model';

export type TimesheetReportType =
  | 'per_employee_per_project'
  | 'per_project_per_period'
  | 'per_employee_per_period';

/** All supported API `output[format]` values across fixed reports. */
export const TimesheetOutputFormat = {
  Cost: 'cost',
  PercentagePerEmployee: 'percentage_per_employee',
  PercentagePerProject: 'percentage_per_project',
  PercentagePerPeriod: 'percentage_per_period',
} as const;

export type TimesheetOutputFormat =
  (typeof TimesheetOutputFormat)[keyof typeof TimesheetOutputFormat];

/** Output value options — Figma Screen 03 / API `output[value]` query param. */
export type TimesheetOutputValue =
  | 'direct_staff_cost'
  | 'all_staff_cost'
  | 'billable_outsource_cost'
  | 'non_billable_outsource_cost'
  | 'billable_cost'
  | 'total_cost'
  | 'selling_cost';

export interface TimesheetSelectOption<T extends string = string> {
  value: T;
  label: string;
}

export const TIMESHEET_OUTPUT_VALUE_OPTIONS: ReadonlyArray<
  TimesheetSelectOption<TimesheetOutputValue>
> = [
  { value: 'direct_staff_cost', label: 'Direct Staff Cost' },
  { value: 'all_staff_cost', label: 'All Staff Cost' },
  { value: 'billable_outsource_cost', label: 'Billable Outsource Cost' },
  {
    value: 'non_billable_outsource_cost',
    label: 'Non Billable Outsource Cost',
  },
  { value: 'billable_cost', label: 'Billable Project Cost' },
  { value: 'total_cost', label: 'Total Project Cost' },
  { value: 'selling_cost', label: 'Selling Project Cost' },
];

export const TIMESHEET_PERIOD_TYPE_OPTIONS: ReadonlyArray<
  TimesheetSelectOption<PeriodType>
> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annually' },
];

export const TIMESHEET_PERIOD_TYPE_FILTER_VALUES: ReadonlyArray<{
  name: string;
  value: PeriodType;
}> = [
  { name: 'Monthly', value: 'monthly' },
  { name: 'Quarterly', value: 'quarterly' },
  { name: 'Annually', value: 'annual' },
];

/**
 * Output format options per report type.
 * Each report exposes Cost + the two percentage formats valid for its axes.
 */
export const TIMESHEET_OUTPUT_FORMAT_OPTIONS: Record<
  TimesheetReportType,
  ReadonlyArray<TimesheetSelectOption<TimesheetOutputFormat>>
> = {
  per_employee_per_project: [
    { value: TimesheetOutputFormat.Cost, label: 'Cost' },
    {
      value: TimesheetOutputFormat.PercentagePerProject,
      label: 'Percentage per X Axis (projects)',
    },
    {
      value: TimesheetOutputFormat.PercentagePerEmployee,
      label: 'Percentage per Y Axis (employees)',
    },
  ],
  per_employee_per_period: [
    { value: TimesheetOutputFormat.Cost, label: 'Cost' },
    {
      value: TimesheetOutputFormat.PercentagePerPeriod,
      label: 'Percentage per X Axis (periods)',
    },
    {
      value: TimesheetOutputFormat.PercentagePerEmployee,
      label: 'Percentage per Y Axis (employees)',
    },
  ],
  per_project_per_period: [
    { value: TimesheetOutputFormat.Cost, label: 'Cost' },
    {
      value: TimesheetOutputFormat.PercentagePerPeriod,
      label: 'Percentage per X Axis (periods)',
    },
    {
      value: TimesheetOutputFormat.PercentagePerProject,
      label: 'Percentage per Y Axis (projects)',
    },
  ],
};

export const TIMESHEET_REPORT_STORAGE_KEYS: Record<
  TimesheetReportType,
  string
> = {
  per_employee_per_project: 'per_employee_per_project_params',
  per_employee_per_period: 'per_employee_per_period_params',
  per_project_per_period: 'per_project_per_period_params',
};

export function getAllowedOutputFormats(
  reportType: TimesheetReportType
): readonly TimesheetOutputFormat[] {
  return TIMESHEET_OUTPUT_FORMAT_OPTIONS[reportType].map(
    option => option.value
  );
}

export function isOutputFormatAllowedForReport(
  reportType: TimesheetReportType,
  format: string
): format is TimesheetOutputFormat {
  return getAllowedOutputFormats(reportType).includes(
    format as TimesheetOutputFormat
  );
}
